import { filterSessionsByCourse } from '../../utils/sessionUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Download, ArrowLeft, CheckCircle, XCircle, AlertCircle, Info, FileDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/api/api';
import { toast } from 'sonner';

const BulkAdmission = () => {
  const navigate = useNavigate();

  // ── CSV value coercion helpers ──────────────────────────────────────────
  // Excel commonly exports amounts with thousands-separators (e.g. "60,000")
  // and dates in DD/MM/YY (Indian/UK convention). `parseFloat("60,000")` is
  // **silently** `60` — that's how 60 000 became 60 in earlier uploads.
  // These helpers normalize BEFORE the value reaches the backend.
  const toNumber = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const cleaned = String(v).replace(/,/g, '').replace(/[₹\s]/g, '').trim();
    if (!cleaned) return 0;
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };
  // Single-value date → ISO YYYY-MM-DD (or '' if unparseable). Falls back to
  // the original string so backend can still attempt to parse if our heuristic
  // is wrong (e.g. user pastes something exotic).
  const toIsoDate = (v) => {
    if (!v) return '';
    const s = String(v).trim();
    if (!s) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
    const m = s.split(/[\/\-.]/);
    if (m.length !== 3) return s;
    let [a, b, c] = m.map(p => parseInt(p, 10));
    if ([a, b, c].some(n => Number.isNaN(n))) return s;
    let day, month, year;
    if (String(m[0]).length === 4) { year = a; month = b; day = c; }
    else {
      day = a; month = b; year = c;
      if (day <= 12 && month > 12) { [day, month] = [month, day]; } // US MM/DD swap
      if (year < 100) year += 2000;
    }
    if (month < 1 || month > 12 || day < 1 || day > 31) return s;
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  };
  // Semicolon-list of dates → ISO list (skips empties)
  const toIsoDateList = (v) => {
    if (!v) return '';
    return String(v)
      .split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .map(toIsoDate)
      .join(';');
  };
  // Semicolon-list of amounts → re-emit with commas stripped so backend gets clean numbers
  const cleanAmountList = (v) => {
    if (!v) return '';
    return String(v)
      .split(';')
      .map(s => s.trim())
      .filter(Boolean)
      .map(s => s.replace(/,/g, '').replace(/[₹\s]/g, ''))
      .join(';');
  };

  const [file, setFile] = useState(null);
  const [bulkCourseId, setBulkCourseId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [uploadResults, setUploadResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [masterData, setMasterData] = useState({
    courses: [],
    sessions: [],
    semesters: [],
    sections: []
  });

  // Download PDF Report
  const downloadPdfReport = async () => {
    if (!uploadResults || uploadResults.successful === 0) {
      toast.error('No successful admissions to generate report');
      return;
    }

    setDownloadingPdf(true);
    try {
      // Get selected course and session names
      const courseSelect = document.getElementById('bulk-course-select');
      const sessionSelect = document.getElementById('bulk-session-select');
      
      const selectedCourse = courseSelect?.options[courseSelect.selectedIndex]?.text || 'All Courses';
      const selectedSession = sessionSelect?.value || 'Current Session';

      // Prepare report data
      const reportData = {
        upload_date: new Date().toLocaleString('en-IN'),
        course_name: selectedCourse !== 'Use course_code from file' ? selectedCourse : 'Multiple Courses',
        session_name: selectedSession !== '' ? selectedSession : 'Multiple Sessions',
        total: uploadResults.total,
        successful: uploadResults.successful,
        failed: uploadResults.failed,
        students: uploadResults.createdStudents || [],
        errors: uploadResults.errors || []
      };

      const response = await api.post('/students/bulk-admission-pdf', reportData, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulk_admission_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('PDF Report downloaded successfully!');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF report');
    } finally {
      setDownloadingPdf(false);
    }
  };

  // Fetch master data for reference
  useEffect(() => {
    const fetchMasterData = async () => {
      try {
        const [coursesRes, sessionsRes, semestersRes, sectionsRes] = await Promise.all([
          api.get('/master-settings/courses'),
          api.get('/master-settings/sessions'),
          api.get('/master-settings/semesters'),
          api.get('/master-settings/sections')
        ]);
        setMasterData({
          courses: coursesRes.data || [],
          sessions: sessionsRes.data || [],
          semesters: semestersRes.data || [],
          sections: sectionsRes.data || []
        });
      } catch (error) {
        console.error('Error fetching master data:', error);
      }
    };
    fetchMasterData();
  }, []);

  const downloadTemplate = () => {
    // Get first available values from master data for sample
    const sampleCourse = masterData.courses[0]?.code || 'B.ED';
    const sampleSession = masterData.sessions[0]?.name || '2025-2027 B.Ed';
    const sampleSection = masterData.sections[0]?.code || 'A';
    
    // Create CSV template - admission_no removed (auto-generated now)
    const headers = [
      'first_name',
      'middle_name',
      'last_name',
      'roll_number',
      'university_id',
      'method_subject',
      'email',
      'phone',
      'whatsapp_no',
      'date_of_birth',
      'gender',
      'religion',
      'caste_category',
      'blood_group',
      'aadhar_no',
      'nationality',
      'class_10_board',
      'class_10_year',
      'class_10_percentage',
      'class_12_board',
      'class_12_year',
      'class_12_percentage',
      'father_name',
      'father_phone',
      'father_email',
      'father_occupation',
      'mother_name',
      'mother_phone',
      'mother_email',
      'mother_occupation',
      'address',
      'city',
      'state',
      'district',
      'pin_code',
      'landmark',
      'guardian_name',
      'guardian_phone',
      'guardian_relation',
      'course_code',
      'semester_number',
      'section_code',
      'admission_date',
      'session',
      'is_lateral',
      'entry_semester',
      'consultant_code',
      'course_fee',
      'admission_fee',
      'fee_paid_till_date',
      'fee_payment_dates',
      'fee_payment_amounts',
      'payment_mode',
      'bank_name',
      'installment_count',
      'installment_dates'
    ];

    const sampleData = [
      'Rahul',
      'Kumar',
      'Sharma',
      '',
      '',
      '',
      'rahul.sharma@example.com',
      '9876543210',
      '9876543210',
      '2005-05-15',
      'Male',
      'Hindu',
      'General',
      'O+',
      '123456789012',
      'Indian',
      'CBSE',
      '2020',
      '85.5',
      'CBSE',
      '2022',
      '78.2',
      'Suresh Sharma',
      '9876543211',
      'suresh@example.com',
      'Business',
      'Sunita Sharma',
      '9876543212',
      'sunita@example.com',
      'Housewife',
      'House No 123, MG Road',
      'Delhi',
      'Delhi',
      'Central Delhi',
      '110001',
      'Near City Mall',
      'Suresh Sharma',
      '9876543211',
      'Father',
      sampleCourse,
      '1',
      sampleSection,
      '2025-01-15',
      sampleSession,
      'No',
      '',
      '',
      '350000',
      '50000',
      '100000',
      '2025-02-10;2025-04-10',
      '60000;40000',
      'Cash',
      '',
      '4',
      '2025-03-15;2025-06-15;2025-09-15;2025-12-15'
    ];

    // Add instruction row
    const instructions = [
      '# Student ID will be auto-generated based on entry type:',
      '# Regular Entry: ILOEBBED2025001',
      '# Lateral Entry (is_lateral=Yes): ILOEBBEADL2025001',
      '# entry_semester: For lateral students only. Which semester they join (e.g., 3). Leave blank to use course default.',
      '# Phone number will be used as password for Student Portal login',
      '# course_fee: Custom course fee (overrides system fee). Leave blank to use system default',
      '# fee_paid_till_date: Total amount already paid before this admission',
      '# fee_payment_dates: Prior-fee payment date(s) separated by semicolon (;). Single date OR multiple. Example: 2025-02-10  OR  2025-02-10;2025-04-10. Leave blank to use admission_date.',
      '# fee_payment_amounts: Per-date amounts (semicolon ;). Must match fee_payment_dates count. Example: 60000;40000. Leave blank to evenly split fee_paid_till_date across dates.',
      '# payment_mode: Cash | UPI | Bank Transfer | Cheque | Card. Default: Cash. Applies to BOTH admission fee + all prior fee payments for this student.',
      '# bank_name: Required for non-Cash modes. Case-insensitive match against existing bank list. Leave blank to use the default bank for that mode.',
      '# installment_count: Number of installments for due fee. Leave blank for no installments',
      '# installment_dates: Custom dates separated by semicolon (;). Example: 2025-03-15;2025-06-15;2025-09-15. Leave blank for auto monthly dates',
      '# Due Fee = course_fee - admission_fee - fee_paid_till_date'
    ].join('\n');

    // Quote any value containing comma/quote/newline so columns never shift (RFC4180)
    const csvEscape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };

    const csvContent = [
      instructions,
      '',
      headers.join(','),
      sampleData.map(csvEscape).join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'bulk_admission_template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (fileExtension !== 'csv' && fileExtension !== 'xlsx') {
        alert('Please upload a CSV or Excel file');
        return;
      }
      setFile(selectedFile);
      setShowResults(false);
    }
  };

  // RFC4180-compliant CSV parser: handles quoted fields containing commas,
  // escaped quotes ("") and newlines inside quotes (Excel-generated files)
  const parseCSVRows = (text) => {
    const rows = [];
    let row = [];
    let field = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
      const c = text[i];
      if (inQuotes) {
        if (c === '"') {
          if (text[i + 1] === '"') { field += '"'; i++; }
          else inQuotes = false;
        } else field += c;
      } else if (c === '"') {
        inQuotes = true;
      } else if (c === ',') {
        row.push(field); field = '';
      } else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') i++;
        row.push(field); field = '';
        if (row.some(v => v.trim() !== '')) rows.push(row);
        row = [];
      } else {
        field += c;
      }
    }
    if (field.trim() !== '' || row.some(v => v.trim() !== '')) {
      row.push(field);
      rows.push(row);
    }
    return rows;
  };

  const parseCSV = (text) => {
    // Strip BOM and filter out comment rows starting with #
    const cleanText = text.replace(/^\uFEFF/, '');
    const allRows = parseCSVRows(cleanText).filter(r => !(r[0] || '').trim().startsWith('#'));
    const students = [];
    const parseErrors = [];
    if (allRows.length === 0) return { students, parseErrors };

    const headers = allRows[0].map(h => h.trim());

    for (let i = 1; i < allRows.length; i++) {
      const values = allRows[i].map(v => v.trim());
      if (values.length < 5) continue; // Skip incomplete rows
      
      const student = {};
      headers.forEach((header, index) => {
        student[header] = values[index] || '';
      });

      // Column count mismatch = data maps to wrong fields. Report instead of silently corrupting.
      if (values.length !== headers.length) {
        parseErrors.push({
          row: i,
          name: `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Row ${i}`,
          error: `Column count mismatch: expected ${headers.length} columns but found ${values.length}. A value probably contains an extra comma — wrap such values in double quotes (e.g. "House No 123, MG Road").`
        });
        continue;
      }
      
      // Determine if lateral entry
      const isLateral = student.is_lateral?.toLowerCase() === 'yes' || student.is_lateral === 'true' || student.is_lateral === '1';
      // Parse entry_semester (only meaningful for lateral entries; backend defaults to course config if blank)
      const entrySemRaw = (student.entry_semester || '').trim();
      const entrySemester = isLateral && entrySemRaw ? parseInt(entrySemRaw, 10) : undefined;
      
      // Transform data to match API format
      // admission_number will be auto-generated by backend
      const transformedStudent = {
        __row: i, // CSV data row number (for error reporting)
        // No admission_number - backend will auto-generate based on is_lateral_entry
        full_name: `${student.first_name} ${student.middle_name || ''} ${student.last_name}`.trim(),
        email: student.email,
        phone: student.phone,
        date_of_birth: toIsoDate(student.date_of_birth),
        gender: student.gender,
        address: student.address,
        course_id: student.course_code, // Will need to lookup
        semester_id: student.semester_number, // Will need to lookup
        section_id: student.section_code, // Will need to lookup
        session: student.session, // Session name for Student ID generation
        admission_date: toIsoDate(student.admission_date) || new Date().toISOString().split('T')[0],
        is_lateral_entry: isLateral, // For Student ID format (Regular vs Lateral)
        entry_semester: entrySemester, // Sem joined at (lateral only)
        is_approved: true,
        is_blocked: false,
        course_fee: toNumber(student.course_fee),
        admission_fee: toNumber(student.admission_fee),
        fee_paid_till_date: toNumber(student.fee_paid_till_date),
        fee_payment_dates: toIsoDateList(student.fee_payment_dates),
        fee_payment_amounts: cleanAmountList(student.fee_payment_amounts),
        payment_mode: student.payment_mode || 'Cash',
        bank_name: student.bank_name || '',
        installment_count: parseInt(student.installment_count) || 0,
        installment_dates: toIsoDateList(student.installment_dates),
        // Extended data
        extended_data: {
          first_name: student.first_name,
          middle_name: student.middle_name,
          last_name: student.last_name,
          roll_number: student.roll_number,
          university_id: student.university_id,
          method_subject: student.method_subject,
          whatsapp_no: student.whatsapp_no,
          religion: student.religion,
          caste_category: student.caste_category,
          blood_group: student.blood_group,
          aadhar_no: student.aadhar_no,
          nationality: student.nationality,
          class_10_board: student.class_10_board,
          class_10_year: student.class_10_year,
          class_10_percentage: student.class_10_percentage,
          class_12_board: student.class_12_board,
          class_12_year: student.class_12_year,
          class_12_percentage: student.class_12_percentage,
          father_name: student.father_name,
          father_phone: student.father_phone,
          father_email: student.father_email,
          father_occupation: student.father_occupation,
          mother_name: student.mother_name,
          mother_phone: student.mother_phone,
          mother_email: student.mother_email,
          mother_occupation: student.mother_occupation,
          city: student.city,
          state: student.state,
          district: student.district,
          pin_code: student.pin_code,
          landmark: student.landmark,
          guardian_name: student.guardian_name,
          guardian_phone: student.guardian_phone,
          guardian_relation: student.guardian_relation,
          consultant_code: student.consultant_code
        }
      };
      
      students.push(transformedStudent);
    }

    return { students, parseErrors };
  };

  // Convert any API error (including FastAPI 422 validation arrays) into a readable string.
  // Rendering raw objects crashes React (blank white screen) — always return a string.
  const extractErrorMessage = (error) => {
    if (error.code === 'ECONNABORTED') {
      return 'Request timed out. The server may still be processing — check the student list before re-uploading to avoid duplicates.';
    }
    const detail = error.response?.data?.detail;
    if (detail == null) return error.message || 'Unknown error';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map((d) => {
        if (typeof d === 'string') return d;
        const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== 'body') : [];
        let prefix = '';
        if (loc[0] === 'students' && typeof loc[1] === 'number') {
          prefix = `Row ${loc[1] + 1} ${loc.slice(2).join('.')}`;
        } else if (loc.length) {
          prefix = loc.join('.');
        }
        return prefix ? `${prefix}: ${d?.msg || ''}` : (d?.msg || JSON.stringify(d));
      }).join('; ');
    }
    if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
    return String(detail);
  };

  const handleUpload = async () => {
    if (!file) {
      alert('Please select a file first');
      return;
    }

    setUploading(true);
    setShowResults(false);

    try {
      // Detect real Excel files (saved/renamed as .xlsx/.xls) — they are binary, not CSV text
      const magic = new Uint8Array(await file.slice(0, 4).arrayBuffer());
      const isXlsx = magic[0] === 0x50 && magic[1] === 0x4b; // 'PK' zip header
      const isOldXls = magic[0] === 0xd0 && magic[1] === 0xcf && magic[2] === 0x11 && magic[3] === 0xe0;
      if (isXlsx || isOldXls) {
        toast.error('This is an Excel file, not a CSV. In Excel use File > Save As > "CSV (Comma delimited) (*.csv)" and upload that file.');
        setUploading(false);
        return;
      }

      const text = await file.text();
      const { students, parseErrors } = parseCSV(text);

      if (students.length === 0 && parseErrors.length === 0) {
        alert('No valid student data found in the file');
        setUploading(false);
        return;
      }

      // Get override values from UI
      const overrideCourseId = document.getElementById('bulk-course-select')?.value || '';
      const overrideSession = document.getElementById('bulk-session-select')?.value || '';
      const overrideAdmissionFee = parseFloat(document.getElementById('bulk-admission-fee')?.value) || 0;

      // Validate required fields per row BEFORE sending — prevents whole-batch 422 rejection
      const validStudents = [];
      const validationErrors = [...parseErrors];
      students.forEach((s) => {
        const missing = [];
        if (!s.extended_data?.first_name) missing.push('first_name');
        if (!s.phone) missing.push('phone');
        if (!overrideCourseId && !s.course_id) missing.push('course_code');
        if (!overrideSession && !s.session) missing.push('session');
        if (missing.length > 0) {
          validationErrors.push({
            row: s.__row,
            name: s.full_name || `Row ${s.__row}`,
            error: `Missing required fields: ${missing.join(', ')}`
          });
        } else {
          validStudents.push(s);
        }
      });

      // Use the new bulk-admission API endpoint
      const results = {
        total: students.length + parseErrors.length,
        successful: 0,
        skipped: 0,
        failed: validationErrors.length,
        errors: [...validationErrors],
        createdStudents: [],
        skippedStudents: []
      };

      try {
        // Transform students to match bulk API format
        const bulkStudents = validStudents.map(s => ({
          first_name: s.extended_data?.first_name || s.full_name?.split(' ')[0] || '',
          last_name: s.extended_data?.last_name || s.full_name?.split(' ').slice(-1)[0] || '',
          father_name: s.extended_data?.father_name || '',
          mother_name: s.extended_data?.mother_name || '',
          roll_number: s.extended_data?.roll_number || '',
          university_id: s.extended_data?.university_id || '',
          method_subject: s.extended_data?.method_subject || '',
          phone: s.phone,
          email: s.email,
          gender: s.gender,
          date_of_birth: s.date_of_birth,
          address: s.address,
          course_id: overrideCourseId || s.course_id,
          session: overrideSession || s.session,
          semester_id: s.semester_id || null,
          section_id: s.section_id || null,
          course_fee: s.course_fee || 0,
          admission_fee: overrideAdmissionFee || s.admission_fee || 0,
          fee_paid_till_date: s.fee_paid_till_date || 0,
          fee_payment_dates: s.fee_payment_dates || null,
          fee_payment_amounts: s.fee_payment_amounts || null,
          payment_mode: s.payment_mode || 'Cash',
          bank_name: s.bank_name || null,
          installment_count: s.installment_count || null,
          installment_dates: s.installment_dates || null,
          total_course_fee: s.course_fee || s.total_course_fee || 0,
          discount_amount: s.discount_amount || 0,
          consultant_id: s.consultant_id || null,
          is_lateral_entry: s.is_lateral_entry || false,
          admission_date: s.admission_date || new Date().toISOString().split('T')[0]
        }));

        // ── Chunked upload ──────────────────────────────────────────────
        // Production reports a 120s proxy-read timeout. Each student in
        // bulk-admission triggers ~8 Mongo reads (session/course/phone/email/
        // roll/uni/fee_structure/bank) + ~8 writes (student, student_user,
        // incomes, cash_register, fee_payments, fee_installments, fee_setting),
        // which at ~100ms cloud-Mongo round-trip is ~18s for 10 students.
        // Keep chunk size CONSERVATIVE so each request finishes well under
        // the timeout even on a slow production day. Bigger chunks were
        // hitting 504 Gateway Timeout on 100-row uploads (see job MRCE).
        const CHUNK_SIZE = 10;
        const REQUEST_TIMEOUT_MS = 180_000; // 3 minutes per chunk safety net
        const allDetails = [];
        let aggSuccess = 0;
        let aggSkip = 0;
        let aggFail = 0;
        let aggTotalProcessed = 0;
        setUploadProgress({ done: 0, total: bulkStudents.length });

        for (let i = 0; i < bulkStudents.length; i += CHUNK_SIZE) {
          const chunk = bulkStudents.slice(i, i + CHUNK_SIZE);
          try {
            const resp = await api.post(
              '/students/bulk-admission',
              { students: chunk },
              { timeout: REQUEST_TIMEOUT_MS }
            );
            const data = resp?.data || {};
            aggSuccess += data.successful || 0;
            aggSkip += data.skipped || 0;
            aggFail += data.failed || 0;
            aggTotalProcessed += data.total || chunk.length;
            // Adjust each detail's row number to refer to the position in the
            // ORIGINAL CSV so the user can locate the failing row easily.
            (data.details || []).forEach(d => {
              const origRow = (d.row || 0) + i;
              allDetails.push({ ...d, row: origRow });
            });
          } catch (chunkErr) {
            // A whole chunk failed (likely network/timeout). Mark every student
            // in this chunk as failed so the user can retry just those rows.
            console.error(`Chunk ${i / CHUNK_SIZE + 1} failed:`, chunkErr);
            aggFail += chunk.length;
            aggTotalProcessed += chunk.length;
            chunk.forEach((s, j) => {
              allDetails.push({
                row: i + j + 1,
                status: 'failed',
                name: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
                message: extractErrorMessage(chunkErr) || `Chunk request failed`,
              });
            });
          }
          setUploadProgress({ done: Math.min(i + CHUNK_SIZE, bulkStudents.length), total: bulkStudents.length });
        }

        if (bulkStudents.length > 0) {
          results.total = aggTotalProcessed + validationErrors.length;
          results.successful = aggSuccess;
          results.skipped = aggSkip;
          results.failed = aggFail + validationErrors.length;

          allDetails.forEach(detail => {
            if (detail.status === 'success') {
              results.createdStudents.push({
                name: detail.name,
                student_id: detail.admission_number,
                login_id: detail.login_id || detail.admission_number,
                default_password: detail.default_password || 'Phone Number',
                is_lateral: detail.admission_number?.includes('L'),
                course_fee: detail.course_fee || 0,
                due_fee: detail.due_fee || 0,
                installments_created: detail.installments_created || 0
              });
            } else if (detail.status === 'skipped') {
              // Already-admitted on a previous run — gray bucket.
              results.skippedStudents.push({
                row: detail.row,
                name: detail.name,
                message: detail.message
              });
            } else {
              results.errors.push({
                row: detail.row,
                name: detail.name,
                error: detail.message
              });
            }
          });
        }
      } catch (error) {
        console.error('Bulk upload error:', error);
        results.errors.push({
          row: 0,
          name: 'All Students',
          error: extractErrorMessage(error)
        });
        results.failed = results.total;
      }

      setUploadResults(results);
      setShowResults(true);

      if (results.successful > 0 || results.skipped > 0) {
        const parts = [];
        if (results.successful) parts.push(`${results.successful} created`);
        if (results.skipped) parts.push(`${results.skipped} skipped (already admitted)`);
        if (results.failed) parts.push(`${results.failed} failed`);
        alert(`Bulk upload complete: ${parts.join(', ')} out of ${results.total}`);
      }
    } catch (error) {
      console.error('Error processing file:', error);
      alert('Error processing file. Please check the format and try again.');
    } finally {
      setUploading(false);
      setUploadProgress({ done: 0, total: 0 });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/students')}
              className="flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Upload className="text-blue-600" size={32} />
              Bulk Student Admission
            </h1>
          </div>
          <p className="text-gray-600 mt-1 ml-14">Upload multiple students using CSV/Excel file</p>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-blue-800 mb-3">How to use Bulk Upload</h2>
        <ol className="list-decimal list-inside space-y-2 text-gray-700">
          <li>Download the template file by clicking the "Download Template" button below</li>
          <li>Fill in the student details in the Excel/CSV file (one student per row)</li>
          <li>Make sure all required fields are filled correctly</li>
          <li>Upload the completed file using the upload section</li>
          <li>Review the upload results and fix any errors if needed</li>
        </ol>
        
        {/* New Student ID Format Info */}
        <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
          <p className="text-sm text-green-800 flex items-start gap-2">
            <CheckCircle size={16} className="mt-0.5" />
            <span>
              <strong>Student ID Auto-Generated:</strong> No need to enter Student ID/Admission Number. 
              System will automatically generate based on entry type:
              <br />• <strong>Regular Entry:</strong> <code className="bg-green-100 px-1 rounded">ILOEBBED2025001</code>
              <br />• <strong>Lateral Entry (is_lateral=Yes):</strong> <code className="bg-green-100 px-1 rounded">ILOEBBEADL2025001</code>
              <br />• <strong>Password:</strong> Student's phone number will be used as login password
            </span>
          </p>
        </div>
        
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-sm text-yellow-800 flex items-start gap-2">
            <AlertCircle size={16} className="mt-0.5" />
            <span><strong>Important:</strong> Use exact values from the reference codes below. Invalid codes will cause upload failures.</span>
          </p>
        </div>
        
        <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded">
          <p className="text-sm text-purple-800 flex items-start gap-2">
            <Info size={16} className="mt-0.5" />
            <span>
              <strong>Fee & Installment Columns (New):</strong>
              <br />• <strong>course_fee:</strong> Custom course fee. Leave blank to use system default from Fee Structure
              <br />• <strong>admission_fee:</strong> Admission fee already collected
              <br />• <strong>fee_paid_till_date:</strong> Any prior fee amount paid before this bulk admission
              <br />• <strong>installment_count:</strong> Number of EMI installments for remaining due fee (leave blank = no installments)
              <br />• <strong>installment_dates:</strong> Custom dates separated by semicolon (;). Example: <code className="bg-purple-100 px-1 rounded">2025-03-15;2025-06-15;2025-09-15</code>. Leave blank = auto monthly from admission date
              <br />• <strong>Due Fee</strong> = course_fee - admission_fee - fee_paid_till_date (auto-calculated)
              <br />• Late fine will be applied from system settings automatically
              <br />• Installments can be edited later from <strong>Fees Management &gt; Fee Setting</strong>
            </span>
          </p>
        </div>
      </div>

      {/* Reference Codes */}
      {(masterData.courses.length > 0 || masterData.sessions.length > 0) && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Info size={20} className="text-blue-600" />
            Available Codes Reference
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {masterData.courses.length > 0 && (
              <div className="bg-white p-4 rounded border">
                <h3 className="font-medium text-gray-700 mb-2">Course Codes</h3>
                <ul className="text-sm text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                  {masterData.courses.map((c, i) => (
                    <li key={i} className="flex justify-between">
                      <span className="font-mono text-blue-600">{c.code}</span>
                      <span className="text-gray-500 truncate ml-2" title={c.name}>{c.name?.substring(0, 15)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {masterData.sessions.length > 0 && (
              <div className="bg-white p-4 rounded border">
                <h3 className="font-medium text-gray-700 mb-2">Sessions (Exact Name)</h3>
                <ul className="text-sm text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                  {masterData.sessions.filter(s => s.is_active).map((s, i) => (
                    <li key={i} className="font-mono text-green-600">{s.name}</li>
                  ))}
                </ul>
              </div>
            )}
            {masterData.semesters.length > 0 && (
              <div className="bg-white p-4 rounded border">
                <h3 className="font-medium text-gray-700 mb-2">Semester Numbers</h3>
                <ul className="text-sm text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                  {[...new Set(masterData.semesters.map(s => s.semester_number))].sort().map((num, i) => (
                    <li key={i} className="font-mono text-purple-600">{num}</li>
                  ))}
                </ul>
              </div>
            )}
            {masterData.sections.length > 0 && (
              <div className="bg-white p-4 rounded border">
                <h3 className="font-medium text-gray-700 mb-2">Section Codes</h3>
                <ul className="text-sm text-gray-600 space-y-1 max-h-32 overflow-y-auto">
                  {masterData.sections.map((s, i) => (
                    <li key={i} className="font-mono text-orange-600">{s.code || s.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Download Template */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Step 1: Download Template</h2>
        <p className="text-gray-600 mb-4">Download the CSV template with all required columns and a sample row</p>
        <Button
          onClick={downloadTemplate}
          className="bg-green-600 hover:bg-green-700 flex items-center gap-2"
        >
          <Download size={20} />
          Download CSV Template
        </Button>
      </div>

      {/* Course Selection for Bulk Upload */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Step 2: Select Course & Session</h2>
        <p className="text-gray-600 mb-4">Select the course and session for all students in the upload file (optional - overrides file values)</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Course (Optional)</label>
            <select
              id="bulk-course-select"
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              defaultValue=""
              onChange={(e) => setBulkCourseId(e.target.value)}
            >
              <option value="">Use course_code from file</option>
              {masterData.courses.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.name} ({course.code})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Session (Optional)</label>
            <select
              id="bulk-session-select"
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              defaultValue=""
            >
              <option value="">Use session from file</option>
              {filterSessionsByCourse(masterData.sessions, bulkCourseId).map((session) => (
                <option key={session.id} value={session.name}>
                  {session.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Admission Fee (Optional)</label>
            <input
              type="number"
              id="bulk-admission-fee"
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              placeholder="Default fee for all"
            />
          </div>
        </div>
      </div>

      {/* Upload File */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Step 3: Upload Filled File</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV or Excel File
            </label>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={handleFileChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-blue-50 file:text-blue-700
                  hover:file:bg-blue-100
                  cursor-pointer"
              />
            </div>
            {file && (
              <p className="mt-2 text-sm text-green-600 flex items-center gap-2">
                <CheckCircle size={16} />
                Selected: {file.name} ({(file.size / 1024).toFixed(2)} KB)
              </p>
            )}
          </div>

          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 flex items-center gap-2"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {uploadProgress.total > 0
                  ? `Uploading ${uploadProgress.done} / ${uploadProgress.total}…`
                  : 'Uploading...'}
              </>
            ) : (
              <>
                <Upload size={20} />
                Upload & Process Students
              </>
            )}
          </Button>

          {/* Progress bar visible during chunked upload */}
          {uploading && uploadProgress.total > 0 && (
            <div className="w-full bg-gray-200 rounded-full h-2.5 mt-3" data-testid="bulk-admission-progress">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all"
                style={{ width: `${Math.min(100, (uploadProgress.done / uploadProgress.total) * 100)}%` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Upload Results */}
      {showResults && uploadResults && (
        <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Upload Results</h2>
          
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="bulk-summary-total">
              <p className="text-sm text-blue-600 font-medium">Total Records</p>
              <p className="text-2xl font-bold text-blue-800">{uploadResults.total}</p>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="bulk-summary-success">
              <p className="text-sm text-green-600 font-medium">Successful</p>
              <p className="text-2xl font-bold text-green-800">{uploadResults.successful}</p>
            </div>
            <div className="bg-gray-100 border border-gray-300 rounded-lg p-4" data-testid="bulk-summary-skipped">
              <p className="text-sm text-gray-600 font-medium">Skipped (Already Admitted)</p>
              <p className="text-2xl font-bold text-gray-700">{uploadResults.skipped || 0}</p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="bulk-summary-failed">
              <p className="text-sm text-red-600 font-medium">Failed</p>
              <p className="text-2xl font-bold text-red-800">{uploadResults.failed}</p>
            </div>
          </div>

          {/* Successfully Created Students */}
          {uploadResults.createdStudents && uploadResults.createdStudents.length > 0 && (
            <div className="mb-6">
              <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <CheckCircle size={20} className="text-green-600" />
                Created Students - Login Credentials
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-green-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Student ID (Login)</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Default Password</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Course Fee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Fee</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Installments</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry Type</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {uploadResults.createdStudents.map((student, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm font-mono text-blue-600 font-semibold">{student.login_id || student.student_id}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{student.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 font-mono bg-yellow-50">{student.default_password || student.phone || 'Phone Number'}</td>
                        <td className="px-4 py-3 text-sm font-medium">₹{(student.course_fee || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-sm font-medium text-red-600">₹{(student.due_fee || 0).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 text-sm">
                          {student.installments_created > 0 ? (
                            <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">{student.installments_created} EMI</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${student.is_lateral ? 'bg-orange-100 text-orange-700' : 'bg-green-100 text-green-700'}`}>
                            {student.is_lateral ? 'Lateral' : 'Regular'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <Info size={16} className="inline mr-2" />
                  <strong>Student Portal Login:</strong> Students can login at <code className="bg-blue-100 px-1 rounded">/student/login</code> using:
                  <br />• <strong>Student ID:</strong> Admission Number (shown above)
                  <br />• <strong>Password:</strong> Phone Number (must change on first login)
                </p>
              </div>
            </div>
          )}

          {/* Skipped (Already Admitted) Details */}
          {uploadResults.skippedStudents && uploadResults.skippedStudents.length > 0 && (
            <div className="mb-6" data-testid="bulk-skipped-table">
              <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-gray-400 text-white text-xs">↷</span>
                Skipped — Already Admitted ({uploadResults.skippedStudents.length})
              </h3>
              <p className="text-xs text-gray-500 mb-2">
                These rows were not re-uploaded because a student with the same phone / email / roll / university ID already exists. Safe to ignore — re-uploading the same CSV will keep skipping them.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {uploadResults.skippedStudents.map((s, idx) => (
                      <tr key={idx} className="bg-gray-50/40">
                        <td className="px-4 py-3 text-sm text-gray-900">{s.row}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{s.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{typeof s.message === 'string' ? s.message : JSON.stringify(s.message ?? '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Error Details */}
          {uploadResults.errors.length > 0 && (
            <div>
              <h3 className="text-md font-semibold text-gray-800 mb-3 flex items-center gap-2">
                <XCircle size={20} className="text-red-600" />
                Failed Records Details
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {uploadResults.errors.map((error, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-sm text-gray-900">{error.row}</td>
                        <td className="px-4 py-3 text-sm text-gray-900">{error.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{error.phone}</td>
                        <td className="px-4 py-3 text-sm text-red-600">{typeof error.error === 'string' ? error.error : JSON.stringify(error.error ?? '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {uploadResults.successful > 0 && (
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                onClick={downloadPdfReport}
                disabled={downloadingPdf}
                className="bg-purple-600 hover:bg-purple-700 flex items-center gap-2"
                data-testid="download-pdf-report-btn"
              >
                {downloadingPdf ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <FileDown size={20} />
                    Download PDF Report
                  </>
                )}
              </Button>
              <Button
                onClick={() => navigate('/students')}
                className="bg-green-600 hover:bg-green-700"
              >
                View All Students
              </Button>
              <Button
                onClick={() => {
                  setFile(null);
                  setShowResults(false);
                  setUploadResults(null);
                }}
                variant="outline"
              >
                Upload Another File
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BulkAdmission;
